import {Component, inject, signal} from '@angular/core';
import {CompetitorArticleSyncService} from "./services/competitor-article-sync.service";
import {
  CompetitorArticleDta,
  CompetitorArticleSyncDialogData,
  SelectDeselectData,
  SyncCompetitorArticleProductCard, SyncCompetitorArticleQueryParams
} from "./types/competitor-article-sync.types";
import {MAT_DIALOG_DATA} from "@angular/material/dialog";
import {BaseModalComponent} from "../../../../../../core/components/modals/base-modal/base-modal.component";
import {MatFormField, MatInput, MatLabel} from "@angular/material/input";
import {FormsModule} from "@angular/forms";
import {MatTab, MatTabChangeEvent, MatTabGroup} from "@angular/material/tabs";
import {tabsData} from "./configs/competitro-article-sync-tabs";
import {MatButton} from "@angular/material/button";
import {CarouselComponent} from "../../../../../../shared/components/ui/carousel/carousel.component";
import {
  CompetitorArticleSyncCardComponent
} from "./components/competitor-article-sync-card/competitor-article-sync-card.component";
import {LoaderComponent} from "../../../../../../shared/components/loader/loader.component";

@Component({
  selector: 'app-competitor-article-sync-modal',
  imports: [
    BaseModalComponent,
    MatFormField,
    MatLabel,
    MatInput,
    FormsModule,
    MatTabGroup,
    MatTab,
    MatButton,
    CarouselComponent,
    CompetitorArticleSyncCardComponent,
    LoaderComponent
  ],
  templateUrl: './competitor-article-sync-modal.component.html',
  styleUrl: './competitor-article-sync-modal.component.scss'
})
export class CompetitorArticleSyncModalComponent extends BaseModalComponent {
  private competitorArticleSyncDialogData = inject<CompetitorArticleSyncDialogData>(MAT_DIALOG_DATA);
  private competitorArticleSyncService: CompetitorArticleSyncService = inject(CompetitorArticleSyncService);

  public readonly tabsData = tabsData;
  public productCardId = signal<number>(this.competitorArticleSyncDialogData.productCardId);
  public selectedTabIndex = signal<number>(0);
  public syncCompetitorArticlesData = signal<Array<CompetitorArticleDta>>([]);
  public competitorArticlesData = signal<Array<CompetitorArticleDta>>([]);
  public searchTerm: string = '';
  public currentIndex: number = 0;
  public selectedData = signal<Array<number>>([]);

  public override async ngOnInit(): Promise<void> {
    super.ngOnInit();
    this.modalStatesService.openModal('competitorArticleSync');
    this.loadingService.setIsLoadingState(true);
    await this.initModal();
  }

  public override ngOnDestroy(): void {
    super.ngOnDestroy();
    this.modalStatesService.closeModal('competitorArticleSync');
  }

  public async handleTabChange(event: MatTabChangeEvent): Promise<void> {
    this.selectedTabIndex.set(event.index);
    this.selectedData.set([]);
    this.searchTerm = '';
    this.loadingService.setIsLoadingState(true);
    const handlers: Record<number, () => Promise<void>> = {
      0: () => this.fetchSyncCompetitorArticlesList(),
      1: () => this.fetchCompetitorArticlesList()
    };

    await handlers[this.selectedTabIndex()]?.();
  }

  public async handleSearch(): Promise<void> {
    this.selectedTabIndex() === 0 ?
      await this.fetchSyncCompetitorArticlesList() :
      await this.fetchCompetitorArticlesList();
  }

  public handleEnterSearch(event: Event): void {
    (event.target as HTMLElement).blur();
  }

  public getDataByTab(): Array<CompetitorArticleDta> {
    return this.selectedTabIndex() === 0 ? this.syncCompetitorArticlesData() : this.competitorArticlesData();
  }

  public handleIndexChange(index: number): void {
    this.currentIndex = index;
  }

  public handleSelectData(data: SelectDeselectData): void {
    this.selectedData.update(ids =>
      data.completed
        ? [...ids, data.id]
        : ids.filter(id => id !== data.id)
    );
  }

  public async handleBtn(): Promise<void> {
    this.loadingService.setIsLoadingState(true);
    const handlers: Record<number, () => Promise<void>> = {
      0: () => this.detachCompetitorArticle(),
      1: () => this.attachCompetitorArticle()
    };

    await handlers[this.selectedTabIndex()]?.();
  }

  private async initModal(): Promise<void> {
    this.selectedData.set([]);
    await this.fetchSyncCompetitorArticlesList();
  }

  private async fetchCompetitorArticlesList(): Promise<void> {
    const params = this.getSyncCompetitorArticleQueryParams();
    const data = await this.competitorArticleSyncService.fetchCompetitorArticlesList(params)
      .catch(() => [])
      .finally(() => this.loadingService.setIsLoadingState(false));
    this.competitorArticlesData.set(data)
  }

  private async fetchSyncCompetitorArticlesList(): Promise<void> {
    const params = this.getSyncCompetitorArticleQueryParams();
    const data = await this.competitorArticleSyncService.fetchSyncCompetitorArticlesList(params)
      .catch(() => [])
      .finally(() => this.loadingService.setIsLoadingState(false));

    this.syncCompetitorArticlesData.set(data)
  }

  private async attachCompetitorArticle(): Promise<void> {
    const params = this.getSyncCompetitorArticleProductCard();
    await this.competitorArticleSyncService.attachCompetitorArticle(params)
      .then(() => this.initModal())
      .finally(() => this.loadingService.setIsLoadingState(false));
  }

  private async detachCompetitorArticle(): Promise<void> {
    const params = this.getSyncCompetitorArticleProductCard();
    await this.competitorArticleSyncService.detachCompetitorArticle(params)
      .then(() => this.initModal())
      .finally(() => this.loadingService.setIsLoadingState(false));
  }

  private getSyncCompetitorArticleQueryParams(): SyncCompetitorArticleQueryParams {
    return {
      productCardId: this.productCardId(),
      ...(this.searchTerm && { search: this.searchTerm })
    };
  }

  private getSyncCompetitorArticleProductCard(): SyncCompetitorArticleProductCard {
    return {
      productCardId: this.productCardId(),
      articles: this.selectedData()
    };
  }
}
